import { useEffect, useState } from 'react'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Explainer } from '../../components/Explainer'
import { ApiError, type PasswordPolicy, accounts } from '../../lib/api'

/**
 * How long this organization lets a password stand.
 *
 * On Accounts rather than Settings, because Settings is per person by its own doc comment and this
 * is a rule the organization applies to everybody. Accounts is already the owner-only screen about
 * who can sign in, which is the same subject.
 *
 * Off unless somebody turns it on, and the card says what off means rather than showing a zero and
 * leaving it to be inferred. That default is what keeps every development stack and every end-to-end
 * run working (D-287), so it is stated on the screen as well as in the schema.
 */
export function PasswordPolicyCard({ onChanged }: { onChanged: () => void }) {
  const [policy, setPolicy] = useState<PasswordPolicy | null>(null)
  const [maxAgeDays, setMaxAgeDays] = useState(0)
  const [warnDays, setWarnDays] = useState(14)
  const [problem, setProblem] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let current = true
    accounts
      .passwordPolicy()
      .then((next) => {
        if (!current) return
        setPolicy(next)
        setMaxAgeDays(next.maxAgeDays)
        setWarnDays(next.warnDays)
      })
      .catch(() => {
        // Owner-only, and this card only renders for an owner. A failure here leaves the controls at
        // their defaults rather than putting an error above a list somebody came to read.
      })
    return () => {
      current = false
    }
  }, [])

  const save = async () => {
    setBusy(true)
    setProblem(null)
    setNotice(null)
    try {
      await accounts.setPasswordPolicy(maxAgeDays, warnDays)
      const next = await accounts.passwordPolicy()
      setPolicy(next)
      setNotice(
        maxAgeDays === 0
          ? 'Passwords in this organization no longer expire.'
          : `Passwords now expire after ${maxAgeDays} days.`
      )
      setAsking(false)
      onChanged()
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'The policy could not be saved.')
      setAsking(false)
    } finally {
      setBusy(false)
    }
  }

  // Asked about only in the direction that costs somebody something. Turning ageing on, or
  // shortening it, can hold people at a change screen; turning it off or lengthening it cannot.
  const tightening = maxAgeDays !== 0 && (policy === null || policy.maxAgeDays === 0 || maxAgeDays < policy.maxAgeDays)

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Password policy</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          {policy && policy.maxAgeDays === 0
            ? 'Passwords in this organization do not expire. Set a maximum age to change that.'
            : `Passwords expire after ${policy?.maxAgeDays ?? maxAgeDays} days.`}
        </p>

        {notice && (
          <div className="banner success" role="status" data-testid="password-policy-saved">
            {notice}
          </div>
        )}

        {problem && (
          <p className="error" role="alert" data-testid="password-policy-problem">
            {problem}
          </p>
        )}

        <div className="toolbar">
          <label className="field">
            Maximum age (days, 0 for never)
            <input
              type="number"
              min={0}
              max={3650}
              value={maxAgeDays}
              onChange={(event) => setMaxAgeDays(Number(event.target.value))}
              data-testid="password-max-age"
            />
          </label>
          <label className="field">
            Warn within (days)
            <input
              type="number"
              min={0}
              max={90}
              value={warnDays}
              onChange={(event) => setWarnDays(Number(event.target.value))}
              data-testid="password-warn-days"
            />
          </label>
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => (tightening ? setAsking(true) : void save())}
            data-testid="save-password-policy"
          >
            Save
          </button>
        </div>

        <Explainer
          summary="What happens to a password that is already older than this?"
          data-testid="password-policy-explainer"
        >
          <p>
            It is held at a change screen the next time that account makes a request, and released as
            soon as a new password is chosen. Nothing is stamped on the account, so setting the
            maximum age back to zero lets everybody straight back in - a number typed wrongly is
            undone by retyping it.
          </p>
          <p>
            An account whose password predates this being recorded has no age to measure and never
            expires. Resetting its password gives it one.
          </p>
        </Explainer>
      </div>

      <ConfirmDialog
        open={asking}
        title="Expire passwords in this organization?"
        confirmLabel={busy ? 'Saving…' : 'Set the policy'}
        busy={busy}
        onConfirm={save}
        onClose={() => setAsking(false)}
        data-testid="password-policy-confirm"
      >
        {/*
          The count is the difference between a policy and a surprise. Typing 9 where 90 was meant is
          a plausible slip, and the only honest way to say what it costs is how many people it stops.
        */}
        <div className="banner warning" role="note" data-testid="password-policy-effect">
          {policy && policy.wouldExpireNow > 0
            ? `${policy.wouldExpireNow} account${policy.wouldExpireNow === 1 ? '' : 's'} already had a password older than the age currently saved. Anybody past the new age is asked for a new password the next time they use the console.`
            : 'Anybody whose password is already older than this is asked for a new one the next time they use the console.'}
        </div>
      </ConfirmDialog>
    </div>
  )
}
